import { events as fallbackEvents, media as fallbackMedia, people as fallbackPeople } from './data';
import type { AuthSession, ChangeRequest, EventItem, MediaItem, Person, PersonFormData, Role } from './types';

type ApiPerson = {
  id: string;
  name: string;
  courtesy_name?: string | null;
  generation: number;
  branch: string;
  region: string;
  gender: '男' | '女';
  birth_date: string;
  death_date?: string | null;
  origin?: string | null;
  biography?: string | null;
  privacy: 'public' | 'family' | 'private';
  status: 'verified' | 'pending' | 'memorial';
};

type ApiRelation = {
  person_id: string;
  related_person_id: string;
  relation_type: 'parent' | 'spouse' | 'child';
};

type ApiAsset = {
  id: string;
  person_id: string;
  object_key?: string;
  title: string;
  asset_type: 'photo' | 'document' | 'video' | 'scan';
  year?: string | null;
  visibility?: 'public' | 'family' | 'private';
};

type ApiEvent = {
  id: string;
  person_id?: string | null;
  event_date: string;
  title: string;
  description?: string | null;
  event_type: 'birth' | 'education' | 'marriage' | 'archive' | 'memorial';
};

export type GenealogyGraph = {
  people: Person[];
  media: MediaItem[];
  events: EventItem[];
  online: boolean;
};

const configuredBase =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'https://yunpu-genealogy-api.fox-1cd.workers.dev';

export function getApiBase() {
  return (localStorage.getItem('yunpu_api_base') || configuredBase || '').replace(/\/$/, '');
}

export function setApiBase(value: string) {
  localStorage.setItem('yunpu_api_base', value.replace(/\/$/, ''));
}

function authHeaders(auth: AuthSession | Role) {
  const session: AuthSession = typeof auth === 'string' ? { role: auth, name: auth } : auth;
  const headers: Record<string, string> = {};
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  return headers;
}

async function request<T>(path: string, auth: AuthSession | Role, options: RequestInit = {}) {
  const base = getApiBase();
  if (!base) throw new Error('API base URL is not configured');
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  Object.entries(authHeaders(auth)).forEach(([key, value]) => headers.set(key, value));
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function toPerson(row: ApiPerson, relations: ApiRelation[], index: number): Person {
  const generationRows = new Map<number, number>();
  fallbackPeople.forEach((person) => generationRows.set(person.generation, (generationRows.get(person.generation) ?? 0) + 1));
  const fallback = fallbackPeople.find((person) => person.id === row.id);
  const sameGenerationIndex = index % Math.max(1, generationRows.get(row.generation) ?? 4);
  return {
    id: row.id,
    name: row.name,
    courtesyName: row.courtesy_name ?? undefined,
    generation: row.generation,
    branch: row.branch,
    region: row.region,
    gender: row.gender,
    birth: row.birth_date,
    death: row.death_date ?? undefined,
    origin: row.origin ?? '',
    biography: row.biography ?? '',
    privacy: row.privacy,
    status: row.status,
    parentIds: relations.filter((relation) => relation.person_id === row.id && relation.relation_type === 'parent').map((relation) => relation.related_person_id),
    spouseIds: relations.filter((relation) => relation.person_id === row.id && relation.relation_type === 'spouse').map((relation) => relation.related_person_id),
    childIds: relations.filter((relation) => relation.person_id === row.id && relation.relation_type === 'child').map((relation) => relation.related_person_id),
    x: fallback?.x ?? 170 + sameGenerationIndex * 170,
    y: fallback?.y ?? 40 + (row.generation - 15) * 120
  };
}

function normalizeTreeLayout(people: Person[]) {
  const cloned = people.map((person) => ({ ...person }));
  const byId = new Map(cloned.map((person) => [person.id, person]));

  cloned.forEach((person) => {
    person.spouseIds.forEach((spouseId) => {
      const spouse = byId.get(spouseId);
      if (!spouse || person.id > spouseId) return;
      const left = person.x <= spouse.x ? person : spouse;
      const right = left === person ? spouse : person;
      const minGap = 190;
      if (right.x < left.x + minGap) right.x = left.x + minGap;
    });
  });

  return cloned;
}

export async function login(role: 'admin' | 'member', password: string) {
  const base = getApiBase();
  if (!base) throw new Error('API base URL is not configured');
  const response = await fetch(`${base}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role, password })
  });
  if (!response.ok) throw new Error(await response.text());
  return response.json() as Promise<AuthSession>;
}

export async function fetchGraph(auth: AuthSession | Role): Promise<GenealogyGraph> {
  try {
    const graph = await request<{
      people: ApiPerson[];
      relationships: ApiRelation[];
      assets: ApiAsset[];
      events: ApiEvent[];
    }>('/api/graph', auth);
    return {
      online: true,
      people: normalizeTreeLayout(graph.people.map((person, index) => toPerson(person, graph.relationships, index))),
      media: graph.assets.map((asset) => ({
        id: asset.id,
        personId: asset.person_id,
        title: asset.title,
        type: asset.asset_type === 'scan' ? 'document' : asset.asset_type,
        year: asset.year ?? '',
        visibility: asset.visibility,
        url: asset.object_key?.startsWith('demo/') ? undefined : `${getApiBase()}/api/assets/${asset.id}`
      })),
      events: graph.events.map((event) => ({
        id: event.id,
        personId: event.person_id ?? undefined,
        year: event.event_date,
        title: event.title,
        description: event.description ?? '',
        type: event.event_type
      }))
    };
  } catch {
    return { online: false, people: fallbackPeople, media: fallbackMedia, events: fallbackEvents };
  }
}

export function toPayload(person: PersonFormData) {
  return {
    ...person,
    deathDate: person.deathDate || null
  };
}

export async function savePerson(auth: AuthSession | Role, person: PersonFormData) {
  const method = person.id ? 'PUT' : 'POST';
  const path = person.id ? `/api/people/${person.id}` : '/api/people';
  return request<{ id: string; status: string }>(path, auth, {
    method,
    body: JSON.stringify(toPayload(person))
  });
}

export async function deletePerson(auth: AuthSession | Role, personId: string) {
  return request<{ ok: boolean }>(`/api/people/${personId}`, auth, { method: 'DELETE' });
}

export async function fetchChangeRequests(auth: AuthSession | Role) {
  return request<ChangeRequest[]>('/api/change-requests?status=pending', auth);
}

export async function reviewChangeRequest(auth: AuthSession | Role, id: string, decision: 'approved' | 'rejected') {
  return request<{ id: string; status: string }>(`/api/change-requests/${id}/review`, auth, {
    method: 'POST',
    body: JSON.stringify({ decision })
  });
}

export async function uploadAsset(auth: AuthSession | Role, input: { personId: string; title: string; year: string; visibility: string; file: File }) {
  const base = getApiBase();
  if (!base) throw new Error('API base URL is not configured');
  const form = new FormData();
  form.set('personId', input.personId);
  form.set('title', input.title);
  form.set('year', input.year);
  form.set('visibility', input.visibility);
  form.set('assetType', input.file.type.startsWith('image/') ? 'photo' : 'document');
  form.set('file', input.file);
  const response = await fetch(`${base}/api/assets`, {
    method: 'POST',
    headers: new Headers(authHeaders(auth)),
    body: form
  });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function openAsset(auth: AuthSession | Role, url?: string) {
  if (!url) return;
  const response = await fetch(url, { headers: new Headers(authHeaders(auth)) });
  if (!response.ok) throw new Error(await response.text());
  const objectUrl = URL.createObjectURL(await response.blob());
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export async function importPeople(auth: AuthSession | Role, people: PersonFormData[]) {
  return request<{ imported: number; ids: string[] }>('/api/import', auth, {
    method: 'POST',
    body: JSON.stringify({ people: people.map(toPayload) })
  });
}

export async function exportGenealogy(auth: AuthSession | Role) {
  return request<unknown>('/api/export', auth);
}
